import { useEffect, useRef } from 'react';

/**
 * What the media keys, the Windows media overlay and a phone's lock screen
 * are told and can do: the Media Session API.
 *
 * Metadata is the library record; the handlers are the same functions the
 * keyboard uses. Feature-detected, so a browser without it gets nothing and
 * no error. The action handlers are read through a ref, so the browser's
 * registration is done once and still calls the latest closures.
 *
 * One limit worth knowing: a session belongs to the frame that is playing
 * audio. The `<audio>` element is this frame's, so Drive, OneDrive, Dropbox,
 * podcasts and SoundCloud get the full treatment; the YouTube, Spotify and
 * Mixcloud embeds play in their own frames, which run their own sessions,
 * and what the media keys do there is theirs.
 */

export interface MediaSessionActions {
  play(): void;
  pause(): void;
  next(): void;
  prev(): void;
  seekBy(seconds: number): void;
}

interface Params {
  /** No metadata is set while this is false. */
  enabled: boolean;
  title: string;
  artist?: string;
  album?: string;
  actions: MediaSessionActions;
}

const defaultSeekSeconds = 10;

export default function useMediaSession({ enabled, title, artist, album, actions }: Params) {
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  useEffect(function registerActions() {
    const session = navigator.mediaSession;

    if (!session) {
      return;
    }

    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ['play', () => actionsRef.current.play()],
      ['pause', () => actionsRef.current.pause()],
      ['previoustrack', () => actionsRef.current.prev()],
      ['nexttrack', () => actionsRef.current.next()],
      [
        'seekbackward',
        (details) => actionsRef.current.seekBy(-(details.seekOffset || defaultSeekSeconds)),
      ],
      [
        'seekforward',
        (details) => actionsRef.current.seekBy(details.seekOffset || defaultSeekSeconds),
      ],
    ];

    for (const [action, handler] of handlers) {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // An action this browser does not know. The rest still register.
      }
    }

    return function unregister() {
      for (const [action] of handlers) {
        try {
          session.setActionHandler(action, null);
        } catch {
          // As above.
        }
      }
    };
  }, []);

  useEffect(
    function describeTrack() {
      const session = navigator.mediaSession;

      if (!session || typeof MediaMetadata === 'undefined') {
        return;
      }

      if (!enabled) {
        session.metadata = null;

        return;
      }

      session.metadata = new MediaMetadata({
        title,
        artist: artist || '',
        album: album || '',
      });
    },
    [enabled, title, artist, album],
  );
}
